import React, { useState, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { campaignsApi, type Deployment } from '../api/campaignsApi';
import type { MetaData, MetaAd } from './types';
import { CollapsibleSection, MonthlyBudgetField, EditableList } from './ReviewComponents';
import { charColor } from './charColor';
import { AssetSlotUploader, type SlotSpec } from '@/modules/media/components/AssetSlotUploader';
import { useManagementMode } from './useManagementMode';
import { Trash2, Check } from 'lucide-react';
import { ConfirmDialog } from '@/shared/ui';
import { useToast } from '@/shared/ui/Toast';
import { campaignKeys } from '../queryKeys';
import styles from './ReviewPanel.module.css';
import patterns from '@/shared/ui/styles/patterns.module.css';

interface Props {
  deployment: Deployment;
  campaignId: string;
  locationId: string;
  practiceId?: string;
  onSave: (platformData: MetaData) => void;
  isSaving: boolean;
}

// Surfaces the user can toggle. Values must match what resolvePlacements()
// in backend/.../metaAds.create.ts understands (it does substring matching).
const PLACEMENT_OPTIONS: { value: string; label: string; platform: 'Facebook' | 'Instagram' }[] = [
  { value: 'facebook_feed', label: 'Feed', platform: 'Facebook' },
  { value: 'facebook_reels', label: 'Reels', platform: 'Facebook' },
  { value: 'facebook_stories', label: 'Stories', platform: 'Facebook' },
  { value: 'instagram_feed', label: 'Feed', platform: 'Instagram' },
  { value: 'instagram_reels', label: 'Reels', platform: 'Instagram' },
  { value: 'instagram_stories', label: 'Stories', platform: 'Instagram' },
  { value: 'instagram_explore', label: 'Explore', platform: 'Instagram' },
];

/**
 * Single-string editable input mirroring EditableList's UX but for a scalar.
 * Used everywhere a Meta ad has a single field (headline, description, etc.).
 * Omit `maxChar` for free-form fields with no platform limit (e.g. the image
 * brief) — the character counter and length cap are then hidden.
 */
function EditableField({
  value,
  maxChar,
  multiline = false,
  placeholder,
  onCommit,
}: {
  value: string;
  maxChar?: number;
  multiline?: boolean;
  placeholder?: string;
  onCommit: (v: string) => void;
}) {
  const [local, setLocal] = useState(value);
  const editingRef = useRef(false);

  useEffect(() => {
    if (!editingRef.current) setLocal(value);
  }, [value]);

  const over = maxChar !== undefined && local.length > maxChar;
  const commonProps = {
    className: `${styles.editableInput} ${over ? styles.inputOver : ''}`,
    value: local,
    placeholder,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setLocal(e.target.value),
    onFocus: () => {
      editingRef.current = true;
    },
    onBlur: () => {
      editingRef.current = false;
      onCommit(local);
    },
    ...(maxChar !== undefined ? { maxLength: maxChar + 20 } : {}),
  };

  return (
    <div className={styles.editableRow}>
      {multiline ? <textarea {...commonProps} rows={3} /> : <input {...commonProps} />}
      {maxChar !== undefined && (
        <span className={styles.charCount} style={{ color: charColor(local.length, maxChar) }}>
          {local.length}/{maxChar}
        </span>
      )}
    </div>
  );
}

/**
 * Editable Meta age range. Meta clamps targeting to 18–65 (65 means "65 and
 * over"), so we clamp on commit to match buildTargeting() in the backend
 * metaAds.create.ts and avoid INVALID_AGE_MIN/MAX on launch.
 */
function AgeRangeField({
  ageMin,
  ageMax,
  disabled,
  onCommit,
}: {
  ageMin: number;
  ageMax: number;
  disabled?: boolean;
  onCommit: (min: number, max: number) => void;
}) {
  const [minLocal, setMinLocal] = useState(String(ageMin));
  const [maxLocal, setMaxLocal] = useState(String(ageMax));
  const editingRef = useRef(false);

  useEffect(() => {
    if (!editingRef.current) {
      setMinLocal(String(ageMin));
      setMaxLocal(String(ageMax));
    }
  }, [ageMin, ageMax]);

  const clamp = (n: number) => Math.max(18, Math.min(65, n));

  const commit = () => {
    editingRef.current = false;
    const min = clamp(parseInt(minLocal, 10) || 18);
    const max = Math.max(min, clamp(parseInt(maxLocal, 10) || 65));
    setMinLocal(String(min));
    setMaxLocal(String(max));
    if (min !== ageMin || max !== ageMax) onCommit(min, max);
  };

  return (
    <div className={styles.ageInputs}>
      <input
        type="number"
        min={18}
        max={65}
        className={styles.ageInput}
        value={minLocal}
        disabled={disabled}
        onFocus={() => {
          editingRef.current = true;
        }}
        onChange={(e) => setMinLocal(e.target.value)}
        onBlur={commit}
      />
      <span className={styles.ageDash}>–</span>
      <input
        type="number"
        min={18}
        max={65}
        className={styles.ageInput}
        value={maxLocal}
        disabled={disabled}
        onFocus={() => {
          editingRef.current = true;
        }}
        onChange={(e) => setMaxLocal(e.target.value)}
        onBlur={commit}
      />
    </div>
  );
}

export function MetaReview({
  deployment,
  campaignId,
  locationId,
  practiceId,
  onSave,
  isSaving,
}: Props) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: campaignKeys.detail(practiceId ?? 'me', campaignId),
    });

  const attachMutation = useMutation({
    mutationFn: (payload: { mediaAssetId: string; role: string }) =>
      campaignsApi.attachAsset(campaignId, deployment.id, payload, practiceId),
    onSuccess: invalidate,
  });

  const detachMutation = useMutation({
    mutationFn: ({ mediaAssetId, role }: { mediaAssetId: string; role: string }) =>
      campaignsApi.detachAsset(campaignId, deployment.id, mediaAssetId, role, practiceId),
    onSuccess: invalidate,
  });

  const deleteAdMutation = useMutation({
    mutationFn: (adIndex: number) =>
      campaignsApi.deleteMetaAd(campaignId, deployment.id, adIndex, practiceId),
    onSuccess: () => {
      invalidate();
      showToast('Ad removed', 'success');
    },
    onError: (err: Error) => showToast(err?.message ?? 'Failed to remove ad', 'error'),
  });

  const [confirmDeleteIdx, setConfirmDeleteIdx] = useState<number | null>(null);
  const isAutopilot = useManagementMode() === 'autopilot';

  // Defensive read: imported/synced Meta campaigns (and legacy PN rows) may carry
  // a partial or absent platformData that isn't the current editable shape.
  // Normalize every field the editor touches so a missing `ads`/`targeting`
  // never crashes the page to blank; show a read-only note when there is nothing
  // editable to show.
  const rawData = deployment.platformData as Partial<MetaData> | null | undefined;
  const ads: MetaAd[] = Array.isArray(rawData?.ads)
    ? (rawData!.ads as MetaAd[]).map((ad) => ({
        name: ad?.name ?? '',
        format: ad?.format ?? 'single_image',
        // Legacy rows stored `primaryText` as a single string before Meta
        // multi-body support landed; normalize to an array.
        primaryText: Array.isArray(ad?.primaryText)
          ? ad.primaryText
          : typeof ad?.primaryText === 'string' && ad.primaryText
            ? [ad.primaryText]
            : [],
        headline: ad?.headline ?? '',
        description: ad?.description ?? '',
        cta: ad?.cta ?? 'LEARN_MORE',
        imageSuggestion: ad?.imageSuggestion,
      }))
    : [];

  if (!rawData || (ads.length === 0 && !rawData.targeting)) {
    return (
      <div className={styles.panel}>
        <p className={styles.sectionHint}>
          This campaign was created outside PracticeNova (or before creative editing was supported),
          so there are no editable creative details here. Manage it directly in Meta Ads Manager;
          its status and budget still sync into PracticeNova.
        </p>
      </div>
    );
  }

  const data: MetaData = {
    campaignName: rawData.campaignName ?? '',
    objective: rawData.objective ?? 'awareness',
    adSetName: rawData.adSetName ?? '',
    targeting: {
      ageMin: rawData.targeting?.ageMin ?? 18,
      ageMax: rawData.targeting?.ageMax ?? 65,
      genders: Array.isArray(rawData.targeting?.genders) ? rawData.targeting!.genders : [],
    },
    placements: Array.isArray(rawData.placements) ? rawData.placements : [],
    dailyBudget: typeof rawData.dailyBudget === 'number' ? rawData.dailyBudget : 0,
    ads,
  };

  const updateField = <K extends keyof MetaData>(field: K, value: MetaData[K]) => {
    onSave({ ...data, [field]: value });
  };

  const updateAd = (index: number, patch: Partial<MetaAd>) => {
    const ads = data.ads.map((ad, i) => (i === index ? { ...ad, ...patch } : ad));
    onSave({ ...data, ads });
  };

  const previewAd = data.ads[0];
  const ageLabel = `${data.targeting.ageMin}–${data.targeting.ageMax}`;
  const genderLabel = data.targeting.genders.join(', ') || 'all';
  const normalizedGenders = data.targeting.genders.map((g) => g.toLowerCase());
  const genderValue =
    normalizedGenders.length === 1 &&
    (normalizedGenders[0] === 'male' || normalizedGenders[0] === 'female')
      ? normalizedGenders[0]
      : 'all';
  const attachments = deployment.assets ?? [];
  const isLive = deployment.deployStatus === 'live';
  const targetingDisabled = isLive || isAutopilot;
  const canDeleteAd = !isLive && !isAutopilot && data.ads.length > 1;

  return (
    <div className={styles.panel}>
      {/* Overview */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Campaign Overview</h3>
        <div className={styles.metaRow}>
          <MonthlyBudgetField
            dailyBudget={data.dailyBudget}
            onSave={(nextDaily) => updateField('dailyBudget', nextDaily)}
            disabled={isLive || isAutopilot}
            helperText={`shared across ${data.ads.length} ad${data.ads.length === 1 ? '' : 's'}`}
          />
          <div className={styles.metaStat}>
            <select
              className={styles.targetingSelect}
              value={data.objective}
              disabled={isLive || isAutopilot}
              onChange={(e) => updateField('objective', e.target.value)}
            >
              <option value="leads_call">Leads — Call Now</option>
              <option value="leads_pixel">Leads — Website (Pixel)</option>
              <option value="traffic">Traffic</option>
              <option value="awareness">Awareness</option>
              <option value="engagement">Engagement</option>
            </select>
            <span className={styles.metaLabel}>Objective</span>
          </div>
          <div className={styles.metaStat}>
            <span className={styles.metaValue}>{data.ads.length}</span>
            <span className={styles.metaLabel}>{data.ads.length === 1 ? 'Ad' : 'Ads'}</span>
          </div>
        </div>
      </section>

      {/* Preview of first ad */}
      {previewAd && (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Ad Preview</h3>
          <div className={styles.adPreview}>
            <div className={styles.adHeadlines}>{previewAd.headline}</div>
            <div className={styles.adDesc}>{previewAd.primaryText[0] ?? ''}</div>
            <div className={styles.adUrl}>{previewAd.cta.replace(/_/g, ' ')}</div>
          </div>
        </section>
      )}

      {/* Targeting */}
      <CollapsibleSection title="Targeting" meta={`${ageLabel} · ${genderLabel}`} defaultOpen>
        <div className={styles.targetingGrid}>
          <div className={styles.targetingField}>
            <span className={styles.metaLabel}>Age range</span>
            <AgeRangeField
              ageMin={data.targeting.ageMin}
              ageMax={data.targeting.ageMax}
              disabled={targetingDisabled}
              onCommit={(min, max) =>
                updateField('targeting', { ...data.targeting, ageMin: min, ageMax: max })
              }
            />
          </div>
          <div className={styles.targetingField}>
            <span className={styles.metaLabel}>Gender</span>
            <select
              className={styles.targetingSelect}
              value={genderValue}
              disabled={targetingDisabled}
              onChange={(e) =>
                updateField('targeting', { ...data.targeting, genders: [e.target.value] })
              }
            >
              <option value="all">All</option>
              <option value="male">Men</option>
              <option value="female">Women</option>
            </select>
          </div>
        </div>
      </CollapsibleSection>

      {/* Placements */}
      <CollapsibleSection
        title="Placements"
        meta={`${data.placements.length} selected`}
        defaultOpen
      >
        <p className={styles.sectionHint}>
          Where your ads will appear. Each ad's uploaded image is used across all selected
          placements.
        </p>
        {(['Facebook', 'Instagram'] as const).map((platform) => (
          <div key={platform} className={styles.placementGroup}>
            <div className={styles.placementGroupLabel}>{platform}</div>
            <div className={styles.placementChecklist}>
              {PLACEMENT_OPTIONS.filter((opt) => opt.platform === platform).map((opt) => {
                const checked = data.placements.includes(opt.value);
                const isLastOne = checked && data.placements.length === 1;
                const disabled = isLive || isAutopilot || isLastOne;
                return (
                  <label
                    key={opt.value}
                    className={`${styles.placementOption} ${disabled ? styles.placementOptionDisabled : ''}`}
                    title={isLastOne ? 'At least one placement is required' : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...data.placements, opt.value]
                          : data.placements.filter((p) => p !== opt.value);
                        updateField('placements', next);
                      }}
                    />
                    <span>{opt.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
        {data.placements.length === 0 && (
          <p className={styles.placementError}>
            At least one placement is required to launch this campaign.
          </p>
        )}
      </CollapsibleSection>

      {/* Ad set name (editable) */}
      <CollapsibleSection title="Ad Set" meta={data.adSetName}>
        <EditableField
          value={data.adSetName}
          maxChar={60}
          onCommit={(v) => updateField('adSetName', v)}
        />
      </CollapsibleSection>

      {/* Per-ad editing */}
      {data.ads.map((ad, i) => {
        // Per-ad image slots. The primary 1:1 keeps the legacy role `ad_<i>`
        // (required and what the Meta launcher uses today). Additional ratios
        // are optional and stored alongside as `ad_<i>_<ratio>` so the
        // launcher can pass them via asset_feed_spec.images for better
        // placement-specific renders.
        const primaryRole = `ad_${i}`;
        const slotDefs: SlotSpec[] = [
          {
            role: primaryRole,
            label: 'Feed (1:1)',
            description:
              ad.imageSuggestion?.description || 'Required — used across feed placements.',
            aspectRatio: '1:1',
          },
          {
            role: `ad_${i}_4x5`,
            label: 'Vertical (4:5)',
            description: 'Optional — fills the vertical feed slot for taller renders.',
            aspectRatio: '4:5',
          },
          {
            role: `ad_${i}_9x16`,
            label: 'Story / Reel (9:16)',
            description: 'Optional — uncropped Story and Reel placements.',
            aspectRatio: '9:16',
          },
        ];
        const hasImage = attachments.some((a) => a.role === primaryRole);
        const filledLabel = hasImage ? (
          <>
            <Check size={12} aria-hidden /> image
          </>
        ) : (
          'image missing'
        );
        return (
          <CollapsibleSection
            key={i}
            title={ad.name || `Ad ${i + 1}`}
            meta={
              <>
                {ad.format} · {ad.cta} · {filledLabel}
              </>
            }
            defaultOpen={i === 0}
            onRenameTitle={(v) => updateAd(i, { name: v })}
          >
            <div className={patterns.fieldStack}>
              <div className={patterns.field}>
                <span className={patterns.fieldLabel}>Image</span>
                <div className={styles.imageSuggestions}>
                  {slotDefs.map((slot, slotIdx) => (
                    <AssetSlotUploader
                      key={slot.role}
                      locationId={locationId}
                      slot={slot}
                      attachment={attachments.find((a) => a.role === slot.role)}
                      disabled={
                        isLive ||
                        isAutopilot ||
                        attachMutation.isPending ||
                        detachMutation.isPending
                      }
                      onAttach={async (mediaAssetId, r) => {
                        await attachMutation.mutateAsync({ mediaAssetId, role: r });
                      }}
                      onDetach={async (mediaAssetId, r) => {
                        await detachMutation.mutateAsync({ mediaAssetId, role: r });
                      }}
                      onRenameDescription={
                        slotIdx === 0
                          ? (v) =>
                              updateAd(i, {
                                imageSuggestion: {
                                  description: v,
                                  aspectRatio: ad.imageSuggestion?.aspectRatio ?? '1:1',
                                },
                              })
                          : undefined
                      }
                    />
                  ))}
                </div>
              </div>
              <div className={patterns.field}>
                <span className={patterns.fieldLabel}>
                  Primary text (up to 5 variants — Meta rotates them)
                </span>
                <EditableList
                  items={ad.primaryText}
                  maxChar={125}
                  onUpdate={(v) => updateAd(i, { primaryText: v })}
                  disabled={isLive || isAutopilot}
                />
              </div>
              <div className={patterns.field}>
                <span className={patterns.fieldLabel}>Headline</span>
                <EditableField
                  value={ad.headline}
                  maxChar={40}
                  onCommit={(v) => updateAd(i, { headline: v })}
                />
              </div>
              <div className={patterns.field}>
                <span className={patterns.fieldLabel}>Description</span>
                <EditableField
                  value={ad.description}
                  maxChar={30}
                  onCommit={(v) => updateAd(i, { description: v })}
                />
              </div>
              <div className={patterns.field}>
                <span className={patterns.fieldLabel}>Call to action</span>
                <select
                  className={styles.targetingSelect}
                  value={ad.cta}
                  disabled={isLive || isAutopilot}
                  onChange={(e) => updateAd(i, { cta: e.target.value })}
                >
                  <option value="LEARN_MORE">Learn More</option>
                  <option value="CONTACT_US">Contact Us</option>
                  <option value="GET_QUOTE">Get Quote</option>
                  <option value="CALL_NOW">Call Now</option>
                  <option value="SIGN_UP">Sign Up</option>
                  <option value="APPLY_NOW">Apply Now</option>
                  <option value="BOOK_NOW">Book Now</option>
                </select>
              </div>
              <div className={styles.adHeaderActions}>
                <button
                  type="button"
                  className={styles.deleteAdButton}
                  onClick={() => setConfirmDeleteIdx(i)}
                  disabled={!canDeleteAd || deleteAdMutation.isPending}
                  title={
                    data.ads.length <= 1
                      ? 'A Meta deployment must keep at least one ad'
                      : isLive
                        ? 'Cannot edit a live deployment'
                        : isAutopilot
                          ? 'Autopilot manages this deployment'
                          : 'Remove this ad'
                  }
                >
                  <Trash2 size={12} />
                  Remove ad
                </button>
              </div>
            </div>
          </CollapsibleSection>
        );
      })}

      {isSaving && <p className={styles.savingNote}>Saving…</p>}

      <ConfirmDialog
        isOpen={confirmDeleteIdx !== null}
        onClose={() => setConfirmDeleteIdx(null)}
        onConfirm={() => {
          if (confirmDeleteIdx !== null) deleteAdMutation.mutate(confirmDeleteIdx);
          setConfirmDeleteIdx(null);
        }}
        title="Remove ad"
        message={
          confirmDeleteIdx !== null
            ? `Remove "${data.ads[confirmDeleteIdx]?.name ?? `Ad ${confirmDeleteIdx + 1}`}" from this Meta deployment? Its attached image will also be detached. The remaining ads continue to share the ad set's daily budget.`
            : ''
        }
        confirmLabel="Remove"
        variant="destructive"
      />
    </div>
  );
}
